import { useEffect, useRef, useState } from "react";
import { BoardCanvas } from "./BoardCanvas";
import { TopHud, BottomHud } from "./Hud";
import { CaptureChoice } from "./CaptureChoice";
import { CapturesStrip } from "./CapturesStrip";
import { HintPanel } from "./HintPanel";
import { VictoryScreen } from "./VictoryScreen";
import { useGame } from "./store";
import { useIsWide } from "./useIsWide";
import { chooseBestAction } from "../bot/minimax";
import { fromWire } from "../../worker/src/serialize";
import type { ServerMessage } from "../../worker/src/protocol";
import type { GameMode, GameOptions } from "./ModeSelect";
import type { OnlineSession } from "./OnlineLobby";
import type { GameState } from "../engine";
import { publicUrl } from "./route";

interface GameScreenProps {
  mode: GameMode;
  options: GameOptions;
  online?: OnlineSession;
  /** When restoring from /g/<id>, the engine state to boot into and the id to reuse. */
  restoredState?: GameState;
  restoredSessionId?: string;
  onExit: () => void;
}

export function GameScreen({ mode, options, online, restoredState, restoredSessionId, onExit }: GameScreenProps) {
  const newGame = useGame((s) => s.newGame);
  const setRemoteGame = useGame((s) => s.setRemoteGame);
  const gameToMove = useGame((s) => s.game.toMove);
  const gameVictory = useGame((s) => s.game.victory);
  const game = useGame((s) => s.game);
  const pending = useGame((s) => s.pending);
  const applyExternalTurn = useGame((s) => s.applyExternalTurn);
  const hintsEnabled = useGame((s) => s.hintsEnabled);
  const startedRef = useRef(false);
  const isWide = useIsWide();
  const [victoryDismissed, setVictoryDismissed] = useState(false);
  // Reset the "View board" dismissal whenever we start a fresh game (sessionId change).
  const sessionIdRef = useRef<string | undefined>(undefined);
  if (sessionIdRef.current !== restoredSessionId) {
    sessionIdRef.current = restoredSessionId;
    if (victoryDismissed) setVictoryDismissed(false);
  }
  const showHints = mode === "bot" && hintsEnabled;

  // Initialize the game once on mount
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    newGame({
      mode,
      humanSide: options.humanSide,
      victoryConfig: options.victoryConfig,
      ...(online ? { initialState: online.initialState, online: online } : {}),
      ...(restoredState ? { initialState: restoredState } : {}),
      ...(restoredSessionId ? { sessionId: restoredSessionId } : {}),
    });
  }, [newGame, mode, options.humanSide, options.victoryConfig, online, restoredState, restoredSessionId]);

  // Online: subscribe to server messages.
  // The worker carries gameRecordId on both `joined` and `state`; mirroring it into the store
  // is what drives the URL update for online games (bot mode populates the same field via the
  // POST /api/games response). We attach the listener via `addEventListener` so we don't clobber
  // the connection-level `message` handler that OnlineLobby already wired up.
  const setOnlineGameRecordId = useGame((s) => s.setOnlineGameRecordId);
  useEffect(() => {
    if (!online) return;
    const handler = (msg: ServerMessage) => {
      if (msg.type === "state") {
        setRemoteGame(fromWire(msg.state));
        if (msg.gameRecordId) setOnlineGameRecordId(msg.gameRecordId);
      } else if (msg.type === "joined" && msg.gameRecordId) {
        setOnlineGameRecordId(msg.gameRecordId);
      }
    };
    online.client.on("message", handler);
    // Note: cleanup is connection-level; closing the client happens on exit.
  }, [online, setRemoteGame, setOnlineGameRecordId]);

  // Bot driver: when it's the bot's turn, take an action after a small delay.
  useEffect(() => {
    if (mode !== "bot") return;
    if (gameVictory) return;
    if (pending) return;
    const botSide = options.humanSide === "white" ? "black" : "white";
    if (gameToMove !== botSide) return;
    const t = setTimeout(() => {
      const turn = chooseBestAction(game, botSide, { deadlineMs: performance.now() + 1500 });
      if (!turn) return;
      applyExternalTurn(turn.move, turn.declared, turn.advance ?? false);
    }, 250);
    return () => clearTimeout(t);
  }, [mode, options.humanSide, gameToMove, gameVictory, pending, game, applyExternalTurn]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        position: "relative",
        backgroundImage: `url(${publicUrl("bg-wood.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#0d0a07",
      }}
    >
      <TopHud onExit={onExit} />
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          justifyContent: "center",
          minHeight: 0,
          // Container queries let BoardCanvas cap its width to a function of this row's height
          // so the strips dock against the actual board edge instead of the screen edge.
          containerType: "size",
        }}
      >
        <CapturesStrip owner="white" />
        <BoardCanvas />
        <CapturesStrip owner="black" />
        {showHints && isWide && <HintPanel layout="sidebar" />}
        <CaptureChoice />
      </div>
      {showHints && !isWide && <HintPanel layout="strip" />}
      <BottomHud />
      {gameVictory && !victoryDismissed && (
        <VictoryScreen
          onExit={onExit}
          onRematch={() => {
            setVictoryDismissed(false);
            newGame({ mode, humanSide: options.humanSide, victoryConfig: options.victoryConfig });
          }}
          onDismiss={() => setVictoryDismissed(true)}
        />
      )}
    </div>
  );
}
